import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../config";
import "./PersonalDetails.css";
import { toast } from "react-toastify";

const PersonalDetails = ({ employee, formSubmitted, setFormSubmitted, onUpdate }) => {
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [personalInfo, setPersonalInfo] = useState({
    fatherName: "",
    motherName: "",
    dateOfBirth: "",
    bloodGroup: "",
    mobileNumber: "",
    emergencyContact: "",
    address: "",
    gender: "", // Added gender field
    nationality: "" // Added nationality field
  });

  // Available blood groups
  const bloodGroups = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
  const genders = ["Male", "Female", "Other"];
  const nationalities = ["Indian", "Other"];

  useEffect(() => {
    if (!employee?._id) return;

    const fetchPersonalInfo = async () => {
      try {
        setLoading(true);
        console.log("📞 PersonalDetails: Fetching personal info for", employee._id);
        
        // Try fetching by emp_id FIRST (this is the key fix)
        let res;
        
        if (employee.emp_id) {
          console.log(`🔍 Trying to fetch by emp_id: ${employee.emp_id}`);
          try {
            res = await axios.get(
              `${API_BASE_URL}/api/personal-info/emp_id/${employee.emp_id}`
            );
            console.log("✅ PersonalDetails: Fetched by emp_id");
          } catch (empIdErr) {
            console.log("⚠️ Could not fetch by emp_id, trying MongoDB ID");
            // Fallback to MongoDB ID
            res = await axios.get(
              `${API_BASE_URL}/api/personal-info/${employee._id}`
            );
          }
        } else {
          // Only MongoDB ID available
          res = await axios.get(
            `${API_BASE_URL}/api/personal-info/${employee._id}`
          );
        }
        
        if (res.data && res.data.success && res.data.data) {
          console.log("✅ PersonalDetails: Received data:", res.data.data);
          const data = res.data.data;
          
          // Format date for input field
          const formattedData = { 
            ...data,
            fatherName: data.fatherName || "",
            motherName: data.motherName || "",
            bloodGroup: data.bloodGroup || "",
            mobileNumber: data.mobileNumber || "",
            emergencyContact: data.emergencyContact || "",
            address: data.address || "",
            gender: data.gender || "",
            nationality: data.nationality || ""
          };
          
          if (data.dateOfBirth) {
            formattedData.dateOfBirth = new Date(data.dateOfBirth).toISOString().split('T')[0];
          }
          
          setPersonalInfo(formattedData);
          setFormSubmitted(true);
          
          // Store data in sessionStorage
          sessionStorage.setItem('personalInfo', JSON.stringify(formattedData));
        } else {
          console.log("⚠️ PersonalDetails: No data or empty response");
          setFormSubmitted(false);
        }
      } catch (err) {
        console.error("❌ PersonalDetails: Error fetching personal info:", err);
        if (err.response?.status === 404) {
          console.log("Personal info not found - this is expected for new employees");
        } else {
          console.error("Full error:", err);
        }
        setFormSubmitted(false);
      } finally {
        setLoading(false);
      }
    };

    fetchPersonalInfo();
  }, [employee, setFormSubmitted]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPersonalInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      const requiredFields = [
        'fatherName', 'motherName', 'dateOfBirth', 
        'bloodGroup', 'mobileNumber', 'emergencyContact', 'address'
      ];
      
      const missingFields = requiredFields.filter(field => !personalInfo[field]?.trim());
      
      if (missingFields.length > 0) {
        toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
        setLoading(false);
        return;
      }

      // Validate mobile numbers
      const mobileRegex = /^[0-9]{10}$/;
      const cleanMobile = personalInfo.mobileNumber.replace(/\D/g, '');
      const cleanEmergency = personalInfo.emergencyContact.replace(/\D/g, '');
      
      if (!mobileRegex.test(cleanMobile)) {
        toast.error("Please enter a valid 10-digit mobile number");
        setLoading(false);
        return;
      }

      if (!mobileRegex.test(cleanEmergency)) {
        toast.error("Please enter a valid 10-digit emergency contact number");
        setLoading(false);
        return;
      }

      // Prepare payload - CRITICAL: Send BOTH IDs
      const payload = {
        fatherName: personalInfo.fatherName.trim(),
        motherName: personalInfo.motherName.trim(),
        dateOfBirth: personalInfo.dateOfBirth ? new Date(personalInfo.dateOfBirth).toISOString() : null,
        bloodGroup: personalInfo.bloodGroup,
        mobileNumber: cleanMobile,
        emergencyContact: cleanEmergency,
        address: personalInfo.address.trim(),
        gender: personalInfo.gender || "Male",
        nationality: personalInfo.nationality || "Indian",
        
        // ✅ SEND BOTH IDs - THIS IS THE KEY FIX
        employeeId: employee._id, // MongoDB ID
        emp_id: employee.emp_id   // Custom emp_id - ADD THIS LINE
      };

      console.log("📤 PersonalDetails: Sending payload with emp_id:", payload);
      console.log("Employee details:", { 
        id: employee._id, 
        emp_id: employee.emp_id, 
        name: employee.name 
      });

      // Use POST instead of PUT - create or update
      const response = await axios.post(
        `${API_BASE_URL}/api/personal-info`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.status === 200 || response.status === 201) {
        console.log("✅ PersonalDetails: Save successful, response:", response.data);
        
        if (response.data.success) {
          toast.success("Personal information saved successfully ✅");
          setFormSubmitted(true);
          setShowForm(false);
          
          // Update state with new data
          const savedData = response.data.data;
          if (savedData) {
            setPersonalInfo(prev => ({
              ...prev,
              ...savedData,
              dateOfBirth: savedData.dateOfBirth ? 
                new Date(savedData.dateOfBirth).toISOString().split('T')[0] : ""
            }));
          }
          
          // Store data in sessionStorage
          sessionStorage.setItem('personalInfo', JSON.stringify(response.data.data));
          
          // Notify parent component about the update
          if (onUpdate) {
            console.log("📢 PersonalDetails: Calling onUpdate callback");
            onUpdate(response.data.data);
          }
          
          // Dispatch event for EmployeeDashboard to refresh
          console.log("📡 PersonalDetails: Dispatching update event");
          window.dispatchEvent(new CustomEvent('personalInfoUpdated', {
            detail: response.data.data
          }));
          
          // Refresh the data after 1 second
          setTimeout(() => {
            // Fetch the updated data
            fetchUpdatedData();
          }, 1000);
        } else {
          toast.error(response.data.message || "Failed to save personal information");
        }
      }
    } catch (err) {
      console.error("❌ PersonalDetails: Error saving personal info:", err);
      console.error("Error response:", err.response?.data);
      
      if (err.response?.data?.message) {
        toast.error(`Failed: ${err.response.data.message}`);
      } else {
        toast.error("Failed to save personal information. Please try again. ❌");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUpdatedData = async () => {
    try {
      if (employee.emp_id) {
        const res = await axios.get(
          `${API_BASE_URL}/api/personal-info/emp_id/${employee.emp_id}`
        );
        if (res.data.success && res.data.data) {
          setPersonalInfo(prev => ({
            ...prev,
            ...res.data.data,
            dateOfBirth: res.data.data.dateOfBirth ? 
              new Date(res.data.data.dateOfBirth).toISOString().split('T')[0] : ""
          }));
        }
      }
    } catch (err) {
      console.log("Could not refresh data:", err);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
  };

  if (loading && !showForm) {
    return <div className="personal-details-container">Loading...</div>;
  }

  return (
    <div className="personal-details-container">
      <div className="info-card">
        <div className="info-header">
          <h4>Personal Information</h4>
          {employee.emp_id && (
            <div className="emp-id-badge">
              ID: {employee.emp_id}
            </div>
          )}
        </div>
        <button
          className={`info-btn ${formSubmitted ? "completed" : "pending"}`}
          onClick={() => setShowForm(true)}
          disabled={loading}
        >
          {loading ? "Loading..." : (formSubmitted ? "View/Edit Info" : "Add Personal Info")}
          {formSubmitted && <span className="status-check">✓</span>}
        </button>
      </div>

      {showForm && (
        <div className="form-modal">
          <div className="form-container">
            <div className="form-header">
              <h3>Personal Information</h3>
              <div className="employee-info">
                <span className="employee-name">{employee.name}</span>
                {employee.emp_id && (
                  <span className="employee-id">ID: {employee.emp_id}</span>
                )}
              </div>
              <button
                className="form-close"
                onClick={handleCancel}
                disabled={loading}
              >
                ✖
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="form-body">
              <div className="form-grid">
                {/* Father's Name */}
                <div className="form-field">
                  <label className="form-label">
                    Father's Name *
                  </label>
                  <input
                    type="text"
                    name="fatherName"
                    value={personalInfo.fatherName || ""}
                    onChange={handleChange}
                    className="form-input"
                    required
                    disabled={loading}
                    placeholder="Enter father's name"
                  />
                </div>

                {/* Mother's Name */}
                <div className="form-field">
                  <label className="form-label">
                    Mother's Name *
                  </label>
                  <input
                    type="text"
                    name="motherName"
                    value={personalInfo.motherName || ""}
                    onChange={handleChange}
                    className="form-input"
                    required
                    disabled={loading}
                    placeholder="Enter mother's name"
                  />
                </div>

                {/* Date of Birth */}
                <div className="form-field">
                  <label className="form-label">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={personalInfo.dateOfBirth || ""}
                    onChange={handleChange}
                    className="form-input"
                    required
                    disabled={loading}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>

                {/* Gender */}
                <div className="form-field">
                  <label className="form-label">
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={personalInfo.gender || ""}
                    onChange={handleChange}
                    className="form-input"
                    disabled={loading}
                  >
                    <option value="">Select Gender</option>
                    {genders.map(gender => (
                      <option key={gender} value={gender}>{gender}</option>
                    ))}
                  </select>
                </div>

                {/* Blood Group */}
                <div className="form-field">
                  <label className="form-label">
                    Blood Group *
                  </label>
                  <select
                    name="bloodGroup"
                    value={personalInfo.bloodGroup || ""}
                    onChange={handleChange}
                    className="form-input"
                    required
                    disabled={loading}
                  >
                    <option value="">Select Blood Group</option>
                    {bloodGroups.map(group => (
                      <option key={group} value={group}>{group}</option>
                    ))}
                  </select>
                </div>

                {/* Nationality */}
                <div className="form-field">
                  <label className="form-label">
                    Nationality
                  </label>
                  <select
                    name="nationality"
                    value={personalInfo.nationality || ""}
                    onChange={handleChange}
                    className="form-input"
                    disabled={loading}
                  >
                    <option value="">Select Nationality</option>
                    {nationalities.map(nat => (
                      <option key={nat} value={nat}>{nat}</option>
                    ))}
                  </select>
                </div>

                {/* Mobile Number */}
                <div className="form-field">
                  <label className="form-label">
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    name="mobileNumber"
                    value={personalInfo.mobileNumber || ""}
                    onChange={handleChange}
                    className="form-input"
                    required
                    disabled={loading}
                    pattern="[0-9]{10}"
                    maxLength="10"
                    placeholder="10-digit mobile number"
                  />
                </div>

                {/* Emergency Contact */}
                <div className="form-field">
                  <label className="form-label">
                    Emergency Contact *
                  </label>
                  <input
                    type="tel"
                    name="emergencyContact"
                    value={personalInfo.emergencyContact || ""}
                    onChange={handleChange}
                    className="form-input"
                    required
                    disabled={loading}
                    pattern="[0-9]{10}"
                    maxLength="10"
                    placeholder="10-digit emergency number"
                  />
                </div>

                {/* Address */}
                <div className="form-field full-width">
                  <label className="form-label">
                    Address *
                  </label>
                  <textarea
                    name="address"
                    value={personalInfo.address || ""}
                    onChange={handleChange}
                    className="form-input"
                    required
                    rows={4}
                    disabled={loading}
                    placeholder="Enter your complete address"
                  />
                </div>
              </div>
              
              <div className="form-actions">
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save Information"}
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
              
              <div className="form-footer">
                <p className="required-note">
                  <span className="required-asterisk">*</span> indicates required fields
                </p>
                <p className="form-note">
                  Note: Your data will be saved and available in the employee dashboard.
                </p>
                <div className="debug-info">
                  <small>Employee ID: {employee._id} | Emp ID: {employee.emp_id || "Not set"}</small>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalDetails;